import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Role } from '../../models/role';

@Injectable({
  providedIn: 'root'
})
export class AuthRoleService {
  private rolesSubject: BehaviorSubject<Role[]> = new BehaviorSubject<Role[]>([]);

  constructor() {
    // Load roles from sessionStorage when the service is initialized.
    this.loadRolesFromSessionStorage();
  }

  // Load roles from sessionStorage.
  private loadRolesFromSessionStorage(): void {
    const rolesJson = sessionStorage.getItem('roles');
    console.log('📦 Contenido de sessionStorage["roles"]:', rolesJson);

    if (rolesJson) {
      try {
        // Try parsing JSON first.
        const parsed = JSON.parse(rolesJson);

        // Validate that the value is an array of objects with roleName.
        if (Array.isArray(parsed) && parsed.length > 0 &&
          typeof parsed[0] === 'object' && 'roleName' in parsed[0]) {
          this.rolesSubject.next(parsed);
          console.log('✅ Roles cargados (formato JSON):', parsed);
        } else {
          // Treat parsed JSON with an unexpected shape as a string.
          console.log('⚠️ JSON parseado pero sin formato esperado, tratando como string...');
          this.parseAsCommaString(rolesJson);
        }
      } catch (error) {
        // Treat invalid JSON as a comma-separated string.
        console.log('⚠️ No es JSON válido, intentando parsear como string separado por comas...');
        this.parseAsCommaString(rolesJson);
      }
    } else {
      console.warn('⚠️ No se encontraron roles en sessionStorage');
      this.rolesSubject.next([]);
    }
  }

  // Parse roles from a comma-separated string.
  private parseAsCommaString(rolesJson: string): void {
    const rolesArray: Role[] = rolesJson.split(',').map((roleName, index) => ({
      roleId: index + 1, // Temporary ID based on the index.
      roleName: roleName.trim()
    }));

    this.rolesSubject.next(rolesArray);
    console.log('✅ Roles cargados (formato string):', rolesArray);

    // Store roles in the expected format for later use.
    sessionStorage.setItem('roles', JSON.stringify(rolesArray));
    console.log('💾 Roles guardados en formato JSON para futuros usos');
  }

  // Return the current roles.
  getRoles(): Role[] {
    return this.rolesSubject.value;
  }

  // Set roles manually when they need to be updated.
  setRoles(roles: Role[]): void {
    sessionStorage.setItem('roles', JSON.stringify(roles));
    this.rolesSubject.next(roles);
  }

  // Reload roles from sessionStorage after external updates.
  reloadRoles(): void {
    this.loadRolesFromSessionStorage();
  }

  // Return an observable that emits role changes.
  getRolesObservable(): Observable<Role[]> {
    return this.rolesSubject.asObservable();
  }

  // Check whether the user has any permitted role.
  hasAnyRole(allowedRoles: string[]): boolean {
    const roles = this.rolesSubject.value;

    if (!roles || roles.length === 0) {
      console.warn('⚠️ AuthRoleService.hasAnyRole - No hay roles disponibles');
      console.warn('📋 Verifica que sessionStorage tenga la clave "roles" con un array de objetos { roleName: "..." }');
      return false;
    }

    const hasRole = roles.some(role => allowedRoles.includes(role.roleName));

    // Log only denied access to simplify debugging.
    if (!hasRole) {
      console.log('❌ hasAnyRole - SIN PERMISO');
      console.log('   Roles del usuario:', roles.map(r => r.roleName));
      console.log('   Roles requeridos:', allowedRoles);
    }

    return hasRole;
  }

  // Check whether the user has a specific role.
  hasRole(roleName: string): boolean {
    const roles = this.rolesSubject.value;
    return roles.some(role => role.roleName === roleName);
  }

  // Clear roles when the user signs out.
  clearRoles(): void {
    sessionStorage.removeItem('roles');
    this.rolesSubject.next([]);
  }
}
